import React, { useState, useEffect, useRef } from 'react';
import './ChatAssistant.css';
import Send from '@mui/icons-material/Send';
import Close from '@mui/icons-material/Close';
import ChatBubbleOutline from '@mui/icons-material/ChatBubbleOutline';
import ErrorIcon from '@mui/icons-material/Error';
import axios from 'axios';

import config from '../../config';
import { useTranslation } from 'react-i18next';
// CRM news base (used to fetch server-side normalized news summaries). Defaults to localhost:6001/api
const CRM_NEWS_BASE = process.env.REACT_APP_CRM_API_URL || 'http://localhost:6001/api';


const ChatAssistant = ({ toggleChatAssistant }) => {
  const { t } = useTranslation();
  const persistedOpen = typeof window !== 'undefined' ? localStorage.getItem('zz_chat_open') === '1' : false;
  const [isOpen, setIsOpen] = useState(persistedOpen);
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [hasOpened, setHasOpened] = useState(false); // Track if chat window has been opened
  const [newsSummary, setNewsSummary] = useState(null);
  const [lastEvents, setLastEvents] = useState(null);
  const [brokenThumbs, setBrokenThumbs] = useState({});
  const textareaRef = useRef(null); // Ref for the textarea
  const chatBodyRef = useRef(null); // Ref for chat body to autoscroll

  // Read persisted city from PostLoginHome (if available)
  let persistedCity = null;
  try {
    const raw = typeof window !== 'undefined' ? localStorage.getItem('zz_location') : null;
    if (raw) {
      const parsed = JSON.parse(raw);
      persistedCity = parsed.city || parsed.cityName || parsed.latitude ? (parsed.city || parsed.cityName || null) : null;
    }
  } catch (e) {
    persistedCity = null;
  }

  const toggleChat = () => {
    setIsOpen(prev => {
      const next = !prev;
      try { localStorage.setItem('zz_chat_open', next ? '1' : '0'); } catch (_) {}
      return next;
    });
  };

  // Ensure we have a stable session id for conversation memory
  const getSessionId = () => {
    try {
      if (typeof window === 'undefined') return null;
      let id = localStorage.getItem('zz_chat_session');
      if (!id) {
        // Simple unique id: timestamp + random
        id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2,9)}`;
        try { localStorage.setItem('zz_chat_session', id); } catch (e) {}
      }
      return id;
    } catch (e) {
      return null;
    }
  };

  const formatMessage = (msg) => {
    return msg
      .split('. ') // Split by full stops to identify sentences
      .map((sentence, index) => {
        // Capitalize first letter of each sentence, except when it starts with 'for'
        const formattedSentence = sentence.charAt(0).toUpperCase() + sentence.slice(1).toLowerCase();

        // Special case for 'for' after a period
        if (formattedSentence.startsWith('For')) {
          return formattedSentence;
        }

        return formattedSentence;
      })
      .join('. '); // Join the sentences back together
  };

  useEffect(() => {
    // On first open, fetch a short local news summary (if available) and then show greeting.
    if (isOpen && !hasOpened) {
      (async () => {
        try {
          // Try to read lat/lon from local storage saved by PostLoginHome
          const raw = typeof window !== 'undefined' ? localStorage.getItem('zz_location') : null;
          if (raw) {
            const loc = JSON.parse(raw);
            const lat = loc.latitude || loc.lat;
            const lon = loc.longitude || loc.lng || loc.lon;
            if (lat && lon) {
              const url = `${CRM_NEWS_BASE.replace(/\/+$/,'')}/news?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}`;
              try {
                const res = await axios.get(url, { withCredentials: true });
                const summary = res?.data?.summary;
                if (summary) {
                  setNewsSummary(summary);
                  // Avoid repeating the page subtitle if the returned summary contains
                  // the same headline text (some news sources may include that copy).
                  const headerLike = /what\s*['’`]?s\s*happening/i;
                  const shouldSkipChatSummary = headerLike.test(String(summary || ''));
                  if (!shouldSkipChatSummary) {
                    setChatHistory(prev => [
                      ...prev,
                      { role: 'assistant', content: `<strong>Local news summary:</strong><br/>${summary}`, timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }
                    ]);
                  }
                }
              } catch (e) {
                // ignore news fetch errors
                // console.warn('chat news fetch failed', e && e.message);
              }
            }
          }
        } catch (err) {
          // ignore parsing errors
        } finally {
          // Always show the friendly greeting after attempting to fetch news
          const initialMessage = {
            role: 'assistant',
            content: `${t('op.floating-button.buttons.chat-assistant-btn.chat-assistant-greetings')}`,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          };
          setChatHistory(prev => [...prev, initialMessage]);
          setHasOpened(true);
        }
      })();
    }
  }, [isOpen, hasOpened, t]);

  // Auto-scroll to the latest message whenever chatHistory changes
  useEffect(() => {
    try {
      const el = chatBodyRef.current;
      if (el) {
        // small timeout to allow new DOM nodes to render
        setTimeout(() => {
          el.scrollTop = el.scrollHeight;
        }, 40);
      }
    } catch (e) {
      // ignore
    }
  }, [chatHistory]);

  // Auto-focus textarea when chat opens
  useEffect(() => {
    if (isOpen) {
      // slight delay to allow animation start
      const id = setTimeout(() => {
        if (textareaRef.current) textareaRef.current.focus();
      }, 120);
      return () => clearTimeout(id);
    }
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        try { localStorage.setItem('zz_chat_open', '0'); } catch (_) {}
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleInputChange = (event) => {
    setMessage(event.target.value);
    // Adjust textarea height based on content
    adjustTextareaHeight();
  };

  const handleSendMessage = async () => {
    if (!message) return;

    const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    // Add user message to chat history
    setChatHistory((prev) => [
      ...prev,
      { role: 'user', content: message, timestamp }
    ]);

    setMessage(''); // Clear the input field
    setLoading(true); // Show loader
    setError(false); // Reset error state

    try {
      // Include the fetched news summary as contextual input if available
      // Build payload: include sessionId and (if available) lat/lon so the CRM can reverse-geocode
      const payload = { message, sessionId: getSessionId() };

      // If we previously fetched a newsSummary or have stored location, include them as context
      if (newsSummary) payload.context = { news: newsSummary };

      // Include lat/lon if available from PostLoginHome stored location — this allows the backend
      // to reverse-geocode and fetch local events/news server-side.
      try {
        const raw = typeof window !== 'undefined' ? localStorage.getItem('zz_location') : null;
        if (raw) {
          const loc = JSON.parse(raw);
          const lat = loc.latitude || loc.lat || null;
          const lon = loc.longitude || loc.lng || loc.lon || null;
          if (lat && lon) {
            payload.lat = lat;
            payload.lon = lon;
          }
        }
      } catch (e) {
        // ignore parse errors
      }

      const response = await axios.post(`${config.CHAT_ASSISTANT_ENDPOINT}`, payload, { withCredentials: true });

      // Support multiple backend response shapes:
      // - { result: 'text' } (old chat assistant service)
      // - { reply: 'text', newsSummary..., events... } (CRM Local Assistant)
      // - raw string
      let rawText = '';
      try {
        if (response && response.data) {
          if (typeof response.data.result === 'string' && response.data.result.trim()) {
            rawText = response.data.result;
          } else if (typeof response.data.reply === 'string' && response.data.reply.trim()) {
            rawText = response.data.reply;
          } else if (typeof response.data === 'string' && response.data.trim()) {
            rawText = response.data;
          } else if (response.data && typeof response.data === 'object') {
            // Prefer a `reply` or `result` field, else attempt to stringify a reasonable fallback
            rawText = response.data.reply || response.data.result || JSON.stringify(response.data);
          }
        }
      } catch (e) {
        rawText = '';
      }

      const assistantMessage = formatMessage(String(rawText || '')) + '<br/>';

      // If the backend returned a structured events array, keep it in state so we can render
      // a beautiful, interactive list in the chat bubble.
      let returnedEvents = null;
      try { returnedEvents = response?.data?.events || null; } catch (e) { returnedEvents = null; }
      if (Array.isArray(returnedEvents) && returnedEvents.length) {
        setLastEvents(returnedEvents);
      } else {
        setLastEvents(null);
      }

      // Add assistant's response to chat history
      setChatHistory((prev) => [
        ...prev,
        { role: 'assistant', content: assistantMessage, timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }
      ]);
    } catch (error) {
      setError(true); // Set error state
    } finally {
      setLoading(false); // Hide loader
    }
  };

  // Handle "Enter" key press to send message
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSendMessage();
    }
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) { // Ensure the textarea element is available
      textarea.style.height = 'auto'; // Reset to let it shrink
      // Use the scrollHeight but cap it to 140px for reasonable UI
      const newHeight = Math.min(textarea.scrollHeight, 140);
      textarea.style.height = `${newHeight}px`;
    }
  };

  const handleCloseChat = () => {
    setIsOpen(false);
    try { localStorage.setItem('zz_chat_open', '0'); } catch (_) {}
  };

  return (
    <div>
      <div className={`fab-container ${!isOpen ? 'animate__animated animate__bounce animate__faster animate__infinite' : ''}`}>
        <button className={`fab-main-button chat-toggle-button ${isOpen ? 'fab-hidden' : ''}`} onClick={toggleChat} title={isOpen ? 'Close chat' : 'Ask'} aria-label={isOpen ? 'Close chat' : 'Ask'}>
          {isOpen ? <Close className='close-icon' /> : <ChatBubbleOutline className='chat-icon' />}
          <span className="chat-label">Chat</span>
        </button>
      </div>

      {isOpen && (
        <div className="chat-window chat-enter" role="dialog" aria-label={t('op.floating-button.buttons.chat-assistant-btn.chat-box-header')} aria-modal="false">
          <div className="chat-header">
            <h3>Local Assistant {persistedCity ? (<span className="location-chip">{persistedCity}</span>) : null}</h3>
            <div className="chat-header-actions">
              <button
                type="button"
                className="close-button"
                onClick={handleCloseChat}
                aria-label={t('op.floating-button.buttons.chat-assistant-btn.close-label', 'Close chat')}
                title={t('op.floating-button.buttons.chat-assistant-btn.close-label', 'Close chat')}
              >
                <Close fontSize="small" />
              </button>
            </div>
          </div>
          {/* Large centered close button for mobile */}
          <button
            type="button"
            className="close-button-large"
            onClick={handleCloseChat}
            aria-label={t('op.floating-button.buttons.chat-assistant-btn.close-label', 'Close chat')}
            title={t('op.floating-button.buttons.chat-assistant-btn.close-label', 'Close chat')}
          >
            <Close fontSize="inherit" />
          </button>
          <div className="chat-body" ref={chatBodyRef}>
            {chatHistory.map((chat, index) => (
              <div key={index} className={chat.role === 'user' ? 'user-message' : 'assistant-message'}>
                <span dangerouslySetInnerHTML={{ __html: chat.content }} />
                <br />
                <div className="timestamp">{chat.timestamp}</div>
              </div>
            ))}

              {/* If the last assistant response included structured events, render them here as colorful mini-cards */}
              {lastEvents && Array.isArray(lastEvents) && lastEvents.length > 0 && (
                <div className="assistant-message events-block">
                  <div className="events-header">Upcoming events</div>
                  <div className="chat-events-list">
                    {lastEvents.map((ev, i) => {
                      const id = ev.id || ev.title || String(i);
                      const img = ev.image || ev.imageUrl || ev.image_url || ev.coverImage || ev.photo || ev.imageLink || ev.thumb || ev.thumbnail || ev.poster || '';
                      const when = ev.start || ev.date || ev.timestamp || '';
                      const whenLabel = when ? new Date(when).toLocaleString() : (ev.venueAddress || ev.location || 'TBD');
                      const desc = (ev.description || ev.summary || '').replace(/\s+/g, ' ').trim();
                      const short = desc.length > 120 ? desc.slice(0, 117).trim() + '…' : desc;
                      const isBroken = brokenThumbs[id];
                      const showThumb = img && !isBroken;
                      return (
                        <div key={id} className="chat-event-card">
                          {showThumb ? (
                            <div className="chat-event-thumb">
                              <img
                                src={img}
                                alt={ev.title || 'event image'}
                                className="chat-event-thumb-img"
                                onError={() => {
                                  // mark this event's thumb as broken so we remove the image slot on re-render
                                  try { setBrokenThumbs(prev => ({ ...prev, [id]: true })); } catch (err) { /* ignore */ }
                                }}
                              />
                            </div>
                          ) : null}
                          <div className="chat-event-body">
                            <div className="chat-event-title">{ev.title || ev.name}</div>
                            <div className="chat-event-meta">{whenLabel}</div>
                            {short ? <div className="chat-event-desc">{short}</div> : null}
                          </div>
                          <div className="chat-event-actions">
                            <button
                              className="event-open-btn"
                              onClick={() => {
                                try {
                                  window.dispatchEvent(new CustomEvent('zz:openEvent', { detail: { event: ev } }));
                                } catch (e) { /* ignore */ }
                              }}
                              aria-label={`Open ${ev.title || ev.name}`}
                            >
                              <span className="assistant-badge">Local</span>
                              View
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            {/* Display loader while waiting for a response */}
            {loading && (
              <div className="loader">
                <div className="dot dot1"></div>
                <div className="dot dot2"></div>
                <div className="dot dot3"></div>
                <div className="dot dot4"></div>
                <div className="dot dot5"></div>
              </div>
            )}

            {/* Display error message if an error occurs */}
            {error && (
              <div className="error-message">
                <ErrorIcon className="error-icon" />
                <span>Oops! Something went wrong. Please try again.</span>
              </div>
            )}
          </div>
          <div className="chat-footer">
            <div className="chat-input-wrapper">
              <textarea
                ref={textareaRef}
                type="text"
                placeholder={t('op.floating-button.buttons.chat-assistant-btn.chat-inputfield-placeholder')}
                value={message}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                className="chat-input"
              ></textarea>
              <button className="send-button" onClick={handleSendMessage}>
                <Send />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatAssistant;